import { getEditorBlockConfig } from "@babackapp/editor-blocks-library/blocks";
import type { VariableDefinition } from "@babackapp/variables-registry";
import {
  EmailsThemeDto,
  PublicShopDto,
} from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import {
  FloatingPanel,
  FloatingPanelHeader,
  FloatingPanelSeparator,
} from "@babackapp/ui/components/floating-panel";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Trash2 } from "lucide-react";
import { useMemo, useRef } from "react";
import { useEditorSelectedBlock } from "../../contexts/editor-selected-block-context";
import { useRemoveBlock } from "../../hooks/use-remove-block";
import {
  type EditorMode,
  EditorContext,
} from "../editor";
import EditorBlockPanelRenderer from "../editor-block-panel-renderer";
import { ShopProvider } from "../shop-provider";

export default function BlockSidebarContent({
  currentLocale,
  defaultLocale,
  localeCodes,
  theme,
  shop,
  variables,
  editorMode = "default",
  hiddenBlocks,
  onToggleBlockVisibility,
  customizedBlockIds,
}: {
  currentLocale: string;
  defaultLocale: string;
  localeCodes: string[];
  theme: EmailsThemeDto;
  shop: PublicShopDto;
  variables: VariableDefinition[];
  editorMode?: EditorMode;
  hiddenBlocks?: string[];
  onToggleBlockVisibility?: (blockId: string) => void;
  customizedBlockIds?: string[];
}) {
  const { selectedBlock, setSelectedBlock } = useEditorSelectedBlock();
  const { removeBlock } = useRemoveBlock();
  const { t } = useTranslation("trackoffice");
  const { t: tEbl } = useTranslation("editor-blocks-library");
  const blockConfig = getEditorBlockConfig(selectedBlock?.type!);
  const Icon = blockConfig?.config?.Icon;
  const containerRef = useRef<HTMLDivElement>(null);

  const isWorkflowMode = editorMode === "workflowCustom";

  const contextValues = useMemo(
    () => ({
      theme,
      currentLocale,
      variables,
      defaultLocale,
      localeCodes,
      editorMode,
      hiddenBlocks,
      onToggleBlockVisibility,
      customizedBlockIds,
    }),
    [
      theme,
      currentLocale,
      variables,
      defaultLocale,
      localeCodes,
      editorMode,
      hiddenBlocks,
      onToggleBlockVisibility,
      customizedBlockIds,
    ],
  );

  if (!selectedBlock || !blockConfig) {
    return null;
  }

  const handleRemoveBlock = () => {
    removeBlock(selectedBlock.id);
    setSelectedBlock(null);
  };

  const handleToggleVisibility = () => {
    onToggleBlockVisibility?.(selectedBlock.id);
    setSelectedBlock(null);
  };

  return (
    <ShopProvider shop={shop}>
      <EditorContext value={contextValues}>
        <div className="relative" ref={containerRef}>
          <FloatingPanel className="overflow-auto w-full">
            <FloatingPanelHeader>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center justify-between text-sm">
                  {Icon && (
                    <Icon className="h-4 w-4 mr-2 text-muted-foreground" />
                  )}
                  {tEbl(blockConfig.config.name)}
                </div>
                {isWorkflowMode ? (
                  <VisibilityToggleButton
                    blockId={selectedBlock.id}
                    hiddenBlocks={hiddenBlocks}
                    onToggle={handleToggleVisibility}
                  />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={handleRemoveBlock}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </FloatingPanelHeader>
            <FloatingPanelSeparator />
            <EditorBlockPanelRenderer
              key={selectedBlock.id}
              block={selectedBlock}
              containerRef={containerRef}
            />
            {blockConfig.config.hasDynamicContent && (
              <>
                <FloatingPanelSeparator />
                <div className="flex flex-col gap-2 p-3 m-2 rounded-md bg-muted/50 text-muted-foreground">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium w-fit">
                    {t("templatesEditor.blocksList.dynamicTag", "dynamic")}
                  </span>
                  <div className="flex gap-2">
                    <p className="text-xs">
                      {t(
                        "blocksEditor.dynamicContentInfo",
                        "This block has dynamic content. Its content will be updated when the email is sent.",
                      )}
                    </p>
                  </div>
                </div>
              </>
            )}
          </FloatingPanel>
        </div>
      </EditorContext>
    </ShopProvider>
  );
}

function VisibilityToggleButton({
  blockId,
  hiddenBlocks,
  onToggle,
}: {
  blockId: string;
  hiddenBlocks?: string[];
  onToggle: () => void;
}) {
  const { t } = useTranslation("trackoffice");
  const isHidden = hiddenBlocks?.includes(blockId) ?? false;

  if (isHidden) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-6 gap-1 px-1.5 text-muted-foreground hover:text-foreground"
        onClick={onToggle}
      >
        <Eye className="h-3.5 w-3.5" />
        <span className="text-xs">
          {t("blocksEditor.showBlock", "Show")}
        </span>
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 gap-1 px-1.5 text-muted-foreground hover:text-foreground"
      onClick={onToggle}
    >
      <EyeOff className="h-3.5 w-3.5" />
      <span className="text-xs">
        {t("blocksEditor.hideBlock", "Hide")}
      </span>
    </Button>
  );
}
